import React from 'react';
import { ArrowLeft } from 'lucide-react';
import MarketingHeader from '../../components/MarketingHeader';
import { REQUEST_ACCESS_URL } from '../../config/links';
import LegalFooter from '../../components/LegalFooter';
import useDocumentMeta from '../../hooks/useDocumentMeta';

const DefenseAerospacePage = () => {

  useDocumentMeta({
    title: 'Factory OS for Defense & Aerospace Prototyping — The Engineering Operating System',
    description:
      "Factory OS is the Engineering Operating System for defense and aerospace prototyping groups: IADT verification at requirement-write time, private projects, end-to-end traceability, and an engineering evidence record that exists the day a program review asks for it. For restricted environments, an ITAR-aligned deployment option with customer-hosted AI inference and embeddings is available on request.",
    path: '/for/defense-aerospace',
  });

  return (
    <div className="fos-notebook min-h-screen">
      <MarketingHeader />

      {/* ══════════ HERO ══════════ */}
      <section className="pt-14 sm:pt-24 pb-10 sm:pb-16 px-5">
        <div className="max-w-[1080px] mx-auto">
          <a href="/" className="inline-flex items-center gap-2 no-underline mb-8" style={{ fontFamily: 'var(--mono)', fontSize: 'var(--t-sm)', color: 'var(--ink-3)', letterSpacing: '0.4px' }}>
            <ArrowLeft size={14} /> Back to Factory OS
          </a>

          <div className="mb-5">
            <span className="nb-eyebrow" style={{ color: 'var(--nb-accent)' }}>
              the engineering operating system · for defense &amp; aerospace prototyping
            </span>
          </div>

          <h1 style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-lg)', lineHeight: 1.05, color: 'var(--ink)', letterSpacing: '-0.5px' }}>
            Built for defense and aerospace prototyping.<br />
            <span className="nb-under">Evidence that survives the review.</span>
          </h1>

          <p className="mt-5 max-w-[680px]" style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink-2)', lineHeight: 1.45 }}>
            Prototyping in regulated environments demands traceability a program review can stand behind. Factory OS captures requirements in IADT verification form, locks evidence to test results, and keeps the rationale for every rejected alternative on the record.
          </p>

          <p className="mt-3 max-w-[680px]" style={{ fontFamily: 'var(--script)', color: 'var(--nb-accent)', fontSize: 'var(--t-md)', lineHeight: 1.2 }}>
            Engineering rigor isn't a slide. It's an audit trail.
          </p>

          <div className="flex flex-wrap gap-3 mt-7">
            <a href={REQUEST_ACCESS_URL} className="nb-btn no-underline text-center">Request access</a>
            <a href="https://demo.factory-os.com" className="nb-btn nb-btn-ghost">▶ Take the 2-minute tour</a>
          </div>

          <div className="mt-4" style={{ fontFamily: 'var(--mono)', fontSize: 'var(--t-sm)', color: 'var(--ink-3)', letterSpacing: '0.4px' }}>
            private projects · IADT verification · requirements-to-test traceability
          </div>
        </div>
      </section>

      {/* ══════════ WHAT A REVIEW DEMANDS ══════════ */}
      <section className="py-12 md:py-[72px] px-5" style={{ borderTop: '1px dashed var(--ink-4)' }}>
        <div className="max-w-[1080px] mx-auto">
          <div className="max-w-[720px] mb-7">
            <div className="nb-eyebrow">what a review demands</div>
            <h2 className="mt-2" style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-lg)', lineHeight: 1.1, color: 'var(--ink)' }}>
              Not a slide deck. A defensible engineering record.
            </h2>
          </div>

          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 list-none p-0 m-0">
            {[
              'Requirements with verification method (IADT) — not bullets on a chart.',
              'Test results that map back to the specific requirement they prove — not a folder of CSVs.',
              "Every alternative considered, with the reason for rejection — not the engineer's memory.",
              'Private project data with controlled access — not a public repo.',
              'Predicted-vs-actual correlation tracked across revisions — not a one-off table.',
              'A traceability matrix that exists on the day the review asks for it — not assembled overnight.',
            ].map((item, i) => (
              <li key={i} className="nb-sketch-thin p-4" style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink-2)', lineHeight: 1.4 }}>
                <span style={{ fontFamily: 'var(--mono)', color: 'var(--ink-4)', marginRight: 8 }}>→</span>{item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ══════════ HOW IT FITS ══════════ */}
      <section className="py-12 md:py-[72px] px-5" style={{ borderTop: '1px dashed var(--ink-4)' }}>
        <div className="max-w-[1080px] mx-auto">
          <div className="max-w-[720px] mb-7">
            <div className="nb-eyebrow" style={{ color: 'var(--nb-accent)' }}>how it fits</div>
            <h2 className="mt-2" style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-lg)', lineHeight: 1.1, color: 'var(--ink)' }}>
              The verification model your reviewers already use.
            </h2>
          </div>

          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 list-none p-0 m-0">
            {[
              ['IADT on every requirement', 'Inspection · Analysis · Demonstration · Test — the verification language MIL-STD-961 and program reviews already speak.'],
              ['Requirements traceability, end-to-end', "From the shall-statement to the predicted value to the measured value. Every requirement has the test result that proved it."],
              ['Private projects by default', 'No public visibility. Your IP stays in your tenant.'],
              ['Trade studies on the record', 'Rejected concepts logged with cost and lead-time deltas. The justification is auditable, not anecdotal.'],
              ['Predicted-vs-actual correlation', 'Model accuracy tracked across revisions. Systematic over- or under-prediction is flagged, not buried.'],
              ['Access controls built for programs', 'Team administration and private projects on Team; SSO, security reviews, and deployment controls on Enterprise. Audit-ready either way.'],
              ['ITAR-aligned deployment (on request)', 'Your models, your boundary: AI inference and embeddings run on locally hosted AI inside your environment, so controlled technical data never leaves it — while your team still works in Factory OS. An Enterprise option we\'re building with design partners.'],
            ].map(([title, body], i) => (
              <li key={i} className="nb-sketch-thin p-4">
                <div style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-md)', color: 'var(--ink)', marginBottom: 6 }}>{title}</div>
                <div style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink-2)', lineHeight: 1.4 }}>{body}</div>
              </li>
            ))}
          </ul>

          <div className="nb-sticky mt-7" style={{ transform: 'rotate(-0.6deg)' }}>
            <b>Program reviews don't care about slides.</b><br />
            They ask three questions: Where is the requirement? What was the verification method? What measured value proved it? Factory OS answers all three on one node, with the version history attached.
          </div>
        </div>
      </section>

      {/* ══════════ CLOSER ══════════ */}
      <section className="py-12 md:py-[72px] px-5 text-center" style={{ borderTop: '1px dashed var(--ink-4)' }}>
        <div className="max-w-[820px] mx-auto">
          <h2 style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-lg)', lineHeight: 1.1, color: 'var(--ink)', marginBottom: 14 }}>
            Walk into the review with the record already built.
          </h2>
          <p className="mb-7" style={{ fontFamily: 'var(--script)', fontSize: 'var(--t-md)', color: 'var(--nb-accent)' }}>
            Stop assembling traceability. Start operating on it.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a href={REQUEST_ACCESS_URL} className="nb-btn no-underline text-center">Request access</a>
            <a href="https://demo.factory-os.com" className="nb-btn nb-btn-ghost">▶ Take the 2-minute tour</a>
          </div>
          <p className="mt-6" style={{ fontFamily: 'var(--mono)', fontSize: 'var(--t-sm)', color: 'var(--ink-3)' }}>
            Programs with specific compliance constraints — including ITAR and export-controlled environments — get in touch and we'll walk through your environment, including the ITAR-aligned deployment option with customer-hosted AI.
          </p>
        </div>
      </section>

      <LegalFooter />
    </div>
  );
};

export default DefenseAerospacePage;
